<template>
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
      >
      <strong class="font-bold">Error! </strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <HowToAgentGroup v-if="showHowToPage"
      :show-how-to-page="showHowToPage"
      :external-navigation="true"
      @show-how-to-close="handleShowHowToPage"
    />

    <div v-if="isStaff || canViewAgentGroup"  class="p-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-6">
        <h1 id="total_count" class="text-3xl font-bold text-gray-900 dark:text-white">
          Agent Groups - {{ filteredAgentGroups.length }}
          <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
        </h1>

        <div class="text-base flex space-x-3 justify-between">
          <button
            type="button"
            class="flex items-center gap-2 animate-shake hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent px-3 py-2 rounded-lg transition-colors"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <path d="M12 17h.01"/>
            </svg>
            <span class="text-sm font-medium dark:text-gray-50 text-gray-900">Tutorial</span>
          </button>
          <button v-if="isStaff || canAddAgentGroup"
            id="add"
            @click="
              $router.push({ name: 'AgentGroupDetails', params: { id: 'new' } })
            "
            class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Agent Group
          </button>
        </div>
        
      </div>
  
      <!-- Search and Filter Section -->
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
      >
        <!-- Search Bar -->
        <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
          <SearchBar
            v-model="searchQuery"
            placeholderText="Search agent groups by name..."
          />
        </div>

        <div class="mb-4 md:hidden">
          <button
            @click="showFilters = !showFilters"
            class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            {{ showFilters ? "Hide Filters" : "Show Filters" }}
          </button>
        </div>
        
        <!-- Filters Section -->
        <div
          :class="[
            'md:flex md:items-center ',
            showFilters ? 'block' : 'hidden md:block',
          ]"
        >
          <FiltersSection
            :filters="dynamicFilters"
            v-model:selectedFilters="selectedFilters"
            :showFilters="showFilters"
            @update:showFilters="showFilters = $event"
            @applyFilters="applyFilters"
          />
        </div>
  
        <!-- Bulk Action Section -->
        <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
          <GlassSelect
            v-model="bulkAction"
            id="bulkactions"
            placeholder="Bulk Actions"
            :options="isStaff || canDeleteAgentGroup ? [{ id: 'delete', name: 'Delete selected items' }] : []"
          />
          <button
            id="apply"
            @click="applyBulkAction"
            :disabled="!bulkAction || selectedAgentGroups.length === 0"
            :class="[
              'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
              bulkAction && selectedAgentGroups.length
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            Apply
          </button>
        </div>
      </div>
  
      <GenericTable
        id="table"
        ref="table"
        :columns="columns"
        :items="paginatedAgentGroups"
        itemKey="id"
        :selectable="true"
        @update:selectedItems="updateSelectedAgentGroups"
        @rowClick="goToAgentGroupDetail"
      >
        <!-- Custom cell templates -->
        <template #created_at="{ value }">
          {{ formatDateTime(value) }}
        </template>
      </GenericTable>
  
      <!-- Pagination -->
      <div
        class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
      >
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Next
          </button>
        </div>
        <div class="text-gray-700 dark:text-white">
          Page {{ currentPage }} of {{ totalPages }}
        </div>
        <div>
          <GlassSelect
            v-model="itemsPerPage"
            @change="updatePagination"
            :options="[{ id: 10, name: '10 per page' }, { id: 20, name: '20 per page' }, { id: 50, name: '50 per page' }, { id: 100, name: '100 per page' }, { id: -1, name: 'All' }]"
          />
        </div>
      </div>
  
      <DeleteModal
        v-if="deletingItem"
        :deleting-item="deletingItem"
        :componet-objects="componetObjects"
        :is-deleting="isDeleting"
        @delete="confirmDelete"
        @close="deletingItem=false"
      />
    </div>
  </template>
  
  <script>
  import { getAgentGroups, deleteAgentGroup } from "@/services/agentGroupService";
  import { getOrgs } from "@/services/wsOrgService";
  import SearchBar from "@/components/SearchBar.vue";
  import GenericTable from "@/components/GenericTable.vue";
  import { useStore } from "@/store";
  import HowToAgentGroup from "./HowToAgentGroup.vue";
  import FiltersSection from "@/components/FiltersSection.vue";
  import DeleteModal from "@/components/DeleteModal.vue";
  import { useTutorial } from "@/composables/tutorial";
  import GlassSelect from "@/components/GlassSelect.vue";

  export default {
    name: "AgentGroupView",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      SearchBar,
      GenericTable,
      HowToAgentGroup,
      FiltersSection,
      DeleteModal,
      GlassSelect
    },
    data() {
      return {
        showFilters: false,
        showHowToPage: false,
        agentgroups: [],
        searchQuery: "",
        filteredAgentGroups: [],
        currentPage: 1,
        itemsPerPage: 10,
        selectedAgentGroups: [],
        deletingItem: false,
        bulkAction: "",
        columns: [
            { key: "name", label: "Name", sortable: true, width: "25%" },
            {
                key: "organization_name",
                label: "Organisation",
                sortable: true,
                width: "20%",
            },
        ],
        error: "",
        orgs: [],
        canAddAgentGroup: false,
        canDeleteAgentGroup: false,
        canViewAgentGroup: false,
        dynamicFilters: [],
        organizations: [],
        selectedFilters: {
          organizationFilter: "",
        },
        steps: [
          {
            element: '#total_count',
            action: 'display',
            value: '',
            message: 'Total count of items in the table.'
          },
          {
            element: '#searchbar',
            action: 'display',
            value: '',
            message: 'Search and filter agent groups.'
          },
          {
            element: '#table',
            action: 'display',
            value: '',
            message: 'Agent groups overview table displaying group name and organisation.'
          },
          {
            element: '#bulkactions',
            action: 'display',
            value: 1,
            message: 'Select one or more items to enable bulk actions.'
          },
          {
            element: '#apply',
            action: 'display',
            message: 'Apply bulk actions (e.g., delete, archive, modify).'
          },
          {
            element: '#add',
            action: 'display',
            message: 'Create a new group.'
          }
        ],
        isDeleting: false
      };
    },
    computed: {
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      isFiltered() {
        return (
          this.searchQuery !== "" ||
          this.selectedFilters.organizationFilter !== ""
        );
      },
      totalPages() {
        return this.itemsPerPage === -1
          ? 1
          : Math.ceil(this.filteredAgentGroups.length / this.itemsPerPage);
      },
      paginatedAgentGroups() {
        if (this.itemsPerPage === -1) {
          return this.filteredAgentGroups;
        }
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredAgentGroups.slice(start, end);
      },
      componetObjects() {
        return this.selectedAgentGroups.map(agentgroup => {
          return {
            values: [
              { name: "Agent Group", value: agentgroup.name },
            ]
          }
        })
      },
    },
    watch: {
      searchQuery() {
        this.applyFilters();
      },
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
    },
    async mounted() {
      await this.fetchOrganisations();
      this.checkPermissions();
      await this.fetchAgentGroups();
      this.initializeFilters();
    },
    methods: {
      async fetchOrganisations(){
        try {
          this.organizations = await getOrgs();
        } catch (error) {
          console.error(error);
        }
      },
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      checkPermissions(){
        this.canAddAgentGroup = useStore.hasPermission("Can add agent groups");
        this.canDeleteAgentGroup = useStore.hasPermission("Can delete agent groups");
        this.canViewAgentGroup = useStore.hasPermission("Can view agent groups");
      },
      async fetchAgentGroups() {
        try {
          const response = await getAgentGroups();
          this.agentgroups = response;  
          this.applyFilters();
        } catch (error) {
          console.error("Error fetching organizations:", error);
          if (error.response) {
            switch (error.response.status) {
              case 404:
                this.error = error.response.status +" The resource was not found. Please check the API endpoint."
                break;
              case 401:
                this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
                break;
              case 403:
                this.error = error.response.status + " You don't have permission to access this resource."
                            + " Contact a staff member to assign you the permissions to access this page. ";
                break;
              default:
                this.error = error.response.status =   error.response.data.detail || "Unknown error"
            }
          } else if (error.request) {
            alert(
              "No response received from the server. Please check your internet connection."
            );
          } else {
            alert(`An error occurred: ${error.message}`);
          }
        }
      },
      goToAgentGroupDetail(agentgroup) {
        this.$router.push({
          name: "AgentGroupDetails",
          params: { id: agentgroup.id },
        });
      },
      updateSelectedAgentGroups(selectedItems) {
        this.selectedAgentGroups = selectedItems;
      },
      async applyBulkAction() {
        if (this.bulkAction === "delete" ) {
          this.deletingItem = true;
        }
        this.bulkAction = "";
      },
      async confirmDelete() {
        this.isDeleting = true;
        await this.deleteSelectedAgentGroups();
        this.deletingItem = false; 
        this.bulkAction = "";
      },
      async deleteSelectedAgentGroups() {
        if (this.selectedAgentGroups.length === 0) {
          alert("No agentgroups selected for deletion");
          return;
        }
    
        try {
          const deletePromises = this.selectedAgentGroups.map((agentgroup) =>
            deleteAgentGroup(agentgroup.id)
          );
          await Promise.all(deletePromises);
  
          this.agentgroups = this.agentgroups.filter(
            (agentgroup) => !this.selectedAgentGroups.some((item) => item.id === agentgroup.id)
          );
          this.$refs.table.clearSelection();
          this.applyFilters();
          this.selectedAgentGroups = [];
        } catch (error) {
          console.error("Error during bulk delete:", error);
          alert(
            "An error occurred while deleting organizations. Please try again."
          );
        } finally {
          this.isDeleting = false;
        }
      },
      formatDateTime(dateString) {
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        };
        return new Date(dateString).toLocaleString(undefined, options);
      },
      async deleteAgentGroupItem(id) {
        if (confirm("Are you sure you want to delete this organization?")) {
          try {
            await deleteAgentGroup(id);
            this.agentgroups = this.agentgroups.filter((agentgroup) => agentgroup.id !== id);
            this.applyFilters(); // Re-apply filters to update the view
          } catch (error) {
            console.error("Error deleting organization:", error);
            // Handle error (e.g., show error message to user)
          }
        }
      },
      applyFilters() {
        this.filteredAgentGroups = this.agentgroups.filter(
          (agentgroup) =>
            agentgroup?.name.toLowerCase().includes(this.searchQuery.toLowerCase()) &&
            (this.selectedFilters.organizationFilter === "" ||
            agentgroup.organization === this.selectedFilters.organizationFilter)
        );
        this.currentPage = 1;
      },
      changePage(delta) {
        this.currentPage += delta;
      },
      updatePagination() {
        this.currentPage = 1;
      },
      initializeFilters() {
        this.dynamicFilters = [
          {
            name: "organizationFilter",
            label: "Organisation",
            options: [{ value: "", label: "All" }].concat(
              Object.keys(this.organizations).map((orgId) => ({
                value: this.organizations[orgId]?.id,
                label: this.organizations[orgId]?.name,
              }))
            ),
          },
        ];
      },
      async startTutorial() {
        await this._start(this.steps);
      }
    },
  };
  </script>
  